import type { AccountSortKey } from '../../types/accountGrid';
import {
  API_CONNECTION_PLATFORMS,
  type ApiConnectionPlatform,
} from '../../types/apiConnections';

// Stage 5A §4 — hardcoded, toggleable column catalogue for the Accounts grid.
// Flat (no groups, unlike the rollout master grid). `alwaysOn` columns can't be
// hidden. Per-user visibility persists via gridPreferences['accounts'] (the
// generalised preferences endpoint); the stored shape is the same string[]
// of visible keys the rollout grid uses, sanitised on read.

export type AccountColumnKey =
  | 'name'
  | 'accountType'
  | 'status'
  | 'accountManagerId'
  | 'mediaSpendPaidBy'
  | 'slug'
  | 'industry'
  | 'currency'
  | 'country'
  | 'parentAccountId'
  | 'metaMasterMonthlyBudget'
  | 'googleMasterMonthlyBudget'
  | 'fbAdAccountId'
  | 'fbAdAccountUrl'
  // DERIVED — shared-ad-account attribution status (§3b). Not a stored field.
  | 'adAccountSharing'
  | 'zernioProfileId'
  | 'defaultInstagramAccountId'
  | 'defaultInstagramAccountName'
  | 'defaultPixelName'
  | 'defaultLeadFormName'
  | 'defaultAudienceCount'
  | 'googleAdsCustomerId'
  | 'googleAdsUrl'
  | 'googleAnalyticsPropertyId'
  | 'salesforceAccountId'
  | 'websiteUrl'
  | 'facebookPageUrl'
  | 'instagramUrl'
  | 'tiktokUrl'
  | 'googleBusinessProfileUrl'
  | 'reportUrl'
  | 'clientDriveUrl'
  | 'internalDriveUrl'
  | 'services'
  | 'runsMeta'
  | 'runsGoogle'
  | 'reportingSyncEnabled'
  | 'description'
  | 'notes'
  | 'targetingNotes'
  | 'leadSyncingNotes'
  | 'billingAddress.suburb'
  | 'billingAddress.state'
  | 'billingAddress.country'
  | 'createdAt'
  | 'updatedAt'
  | 'statusChangedAt'
  // DERIVED — API connection status (cache only). One per platform.
  | 'apiConnections.metaads'
  | 'apiConnections.facebook'
  | 'apiConnections.instagram'
  | 'apiConnections.googlebusiness'
  | 'apiConnections.googleads'
  | 'apiConnections.tiktok';

export type AccountColumnDef = {
  key: AccountColumnKey;
  label: string;
  alwaysOn?: boolean;
  // The server sort key this column maps to. Absent -> column isn't sortable
  // (free-text / URL columns). accountManagerId -> 'accountManager' (resolved
  // name), parentAccountId -> 'parentAccount' (resolved name).
  sortKey?: AccountSortKey;
};

export const ACCOUNTS_COLUMNS: AccountColumnDef[] = [
  // Always-on
  { key: 'name', label: 'Name', alwaysOn: true, sortKey: 'name' },
  { key: 'accountType', label: 'Type', alwaysOn: true, sortKey: 'accountType' },
  { key: 'status', label: 'Status', alwaysOn: true, sortKey: 'status' },
  // Assignment
  { key: 'accountManagerId', label: 'Account Manager', sortKey: 'accountManager' },
  { key: 'mediaSpendPaidBy', label: 'Media Spend', sortKey: 'mediaSpendPaidBy' },
  // Identity
  { key: 'slug', label: 'Slug', sortKey: 'slug' },
  { key: 'industry', label: 'Industry', sortKey: 'industry' },
  { key: 'currency', label: 'Currency', sortKey: 'currency' },
  // Authoritative operating country (reporting §3a) — distinct from the billing
  // address 'Billing Country' below. This is what the reporting dashboard's
  // Country filter resolves from.
  { key: 'country', label: 'Country', sortKey: 'country' },
  { key: 'parentAccountId', label: 'Parent Account', sortKey: 'parentAccount' },
  // Financial
  { key: 'metaMasterMonthlyBudget', label: 'Meta Budget', sortKey: 'metaMasterMonthlyBudget' },
  { key: 'googleMasterMonthlyBudget', label: 'Google Budget', sortKey: 'googleMasterMonthlyBudget' },
  // Integrations
  { key: 'fbAdAccountId', label: 'FB Ad Account', sortKey: 'fbAdAccountId' },
  { key: 'fbAdAccountUrl', label: 'FB Ad URL' },
  // DERIVED (§3b) — shared / not-shared / shared-no-token attribution readiness.
  // Never sortable (it's a roster-wide derivation, not a stored field).
  { key: 'adAccountSharing', label: 'Ad account sharing' },
  { key: 'zernioProfileId', label: 'Zernio Profile' },
  { key: 'defaultInstagramAccountId', label: 'Instagram Account ID' },
  { key: 'defaultInstagramAccountName', label: 'Default IG' },
  // Feature A defaults (names denormalised on the account; audience count
  // resolved server-side). Pixel = name only; Lead Form + Audiences carry a
  // wrench deep-linking to Meta Setup (Forms / Audiences) for that account.
  { key: 'defaultPixelName', label: 'Default Pixel' },
  { key: 'defaultLeadFormName', label: 'Default Lead Form' },
  { key: 'defaultAudienceCount', label: 'Default Audiences' },
  { key: 'googleAdsCustomerId', label: 'Google Ads ID', sortKey: 'googleAdsCustomerId' },
  { key: 'googleAdsUrl', label: 'Google Ads URL' },
  { key: 'googleAnalyticsPropertyId', label: 'GA4 Property' },
  { key: 'salesforceAccountId', label: 'Salesforce ID', sortKey: 'salesforceAccountId' },
  // URLs
  { key: 'websiteUrl', label: 'Website' },
  { key: 'facebookPageUrl', label: 'Facebook Page' },
  { key: 'instagramUrl', label: 'Instagram' },
  { key: 'tiktokUrl', label: 'TikTok' },
  { key: 'googleBusinessProfileUrl', label: 'Google Business' },
  { key: 'reportUrl', label: 'Report URL' },
  { key: 'clientDriveUrl', label: 'Client Drive' },
  { key: 'internalDriveUrl', label: 'Internal Drive' },
  // Services & flags
  { key: 'services', label: 'Services' },
  { key: 'runsMeta', label: 'Runs Meta', sortKey: 'runsMeta' },
  { key: 'runsGoogle', label: 'Runs Google', sortKey: 'runsGoogle' },
  // Build A recurring-reporting-sync opt-in. Off by default; a ✓ / — boolean cell.
  { key: 'reportingSyncEnabled', label: 'Reporting sync' },
  // Free text
  { key: 'description', label: 'Description' },
  { key: 'notes', label: 'Notes' },
  { key: 'targetingNotes', label: 'Targeting Notes' },
  { key: 'leadSyncingNotes', label: 'Lead Syncing Notes' },
  // Address
  { key: 'billingAddress.suburb', label: 'Suburb', sortKey: 'billingAddress.suburb' },
  { key: 'billingAddress.state', label: 'State', sortKey: 'billingAddress.state' },
  { key: 'billingAddress.country', label: 'Address Country', sortKey: 'billingAddress.country' },
  // Audit
  { key: 'createdAt', label: 'Created', sortKey: 'createdAt' },
  { key: 'updatedAt', label: 'Updated', sortKey: 'updatedAt' },
  { key: 'statusChangedAt', label: 'Status Changed', sortKey: 'statusChangedAt' },
  // API connection status (DERIVED — cache only; never sortable). Labels carry a
  // "Connected" framing to disambiguate from the URL columns above (a "Facebook
  // Page" URL column already exists). A cell shows ✓ (connected), — (cached but
  // not connected), or · (status not cached for this page load).
  { key: 'apiConnections.metaads', label: 'Meta Connected' },
  { key: 'apiConnections.facebook', label: 'FB Page Connected' },
  { key: 'apiConnections.instagram', label: 'IG Connected' },
  { key: 'apiConnections.googlebusiness', label: 'GMB Connected' },
  { key: 'apiConnections.googleads', label: 'Google Ads Connected' },
  { key: 'apiConnections.tiktok', label: 'TikTok Connected' },
];

// DERIVED connection columns: map a column key <-> its platform. Used by the
// cell renderer to pull the right boolean out of row.apiConnections.
const API_CONNECTION_COLUMN_PREFIX = 'apiConnections.';

export function apiConnectionColumnKey(platform: ApiConnectionPlatform): AccountColumnKey {
  return `${API_CONNECTION_COLUMN_PREFIX}${platform}` as AccountColumnKey;
}

export function connectionPlatformForColumn(key: string): ApiConnectionPlatform | null {
  if (!key.startsWith(API_CONNECTION_COLUMN_PREFIX)) return null;
  const platform = key.slice(API_CONNECTION_COLUMN_PREFIX.length) as ApiConnectionPlatform;
  return (API_CONNECTION_PLATFORMS as readonly string[]).includes(platform) ? platform : null;
}

export const ACCOUNTS_FACTORY_ORDER: AccountColumnKey[] = ACCOUNTS_COLUMNS.map((c) => c.key);
export const ACCOUNTS_ALL_KEYS: Set<AccountColumnKey> = new Set(ACCOUNTS_FACTORY_ORDER);
export const ACCOUNTS_ALWAYS_ON: AccountColumnKey[] = ACCOUNTS_COLUMNS.filter(
  (c) => c.alwaysOn,
).map((c) => c.key);

// Default visible set (§4): Name, Type, Status, Account Manager, Meta Budget,
// Google Budget, Updated.
export const ACCOUNTS_DEFAULT_VISIBLE: AccountColumnKey[] = [
  'name',
  'accountType',
  'status',
  'accountManagerId',
  'metaMasterMonthlyBudget',
  'googleMasterMonthlyBudget',
  'updatedAt',
];

const COLUMN_BY_KEY = new Map<AccountColumnKey, AccountColumnDef>(
  ACCOUNTS_COLUMNS.map((c) => [c.key, c]),
);

export function accountColumnDef(key: AccountColumnKey): AccountColumnDef {
  const def = COLUMN_BY_KEY.get(key);
  if (!def) throw new Error(`Unknown account column key: ${key}`);
  return def;
}

// Sanitise a stored visible-column list: drop unknown keys, force always-on
// columns in, dedupe, and order by the factory sequence. Mirrors the rollout
// grid's sanitizeOrder posture (forward/backward compatible with catalogue
// changes) but for a flat, set-based (visible) model rather than an ordered one.
export function sanitizeVisible(stored: readonly string[]): AccountColumnKey[] {
  const set = new Set<AccountColumnKey>();
  for (const k of stored) {
    if (ACCOUNTS_ALL_KEYS.has(k as AccountColumnKey)) set.add(k as AccountColumnKey);
  }
  for (const k of ACCOUNTS_ALWAYS_ON) set.add(k);
  return ACCOUNTS_FACTORY_ORDER.filter((k) => set.has(k));
}
